/** @module csi/datasheet/CanvasWidget */
define([
	"dojo/_base/declare",
	"dojo/_base/fx",
	"dojo/_base/lang",
	"dojo/dom-construct",
	"dojo/dom-geometry",
	"dojo/promise/all",
	"dojo/store/Memory",
	"dojo/topic",
	"underscore",
	"canvas/widgets/interfaces/model/enumerations/CanvasMode",
	"csi",
	"csi/_base/Deferred",
	"csi/action",
	"csi/action/Controller",
	"csi/action/FormData",
	"csi/api",
	"csi/canvas/ImageReview",
	"csi/canvas/storeFactory",
	"csi/config",
	"csi/core/ExtendedPath",
	"csi/def",
	"csi/def/Action",
	"csi/def/Relationship",
	"csi/gallery/Widget",
	"csi/heading/_base",
	"csi/html",
	"csi/html/ViewRef",
	"csi/locale",
	"csi/message",
	"csi/middleware/attributeRule/RuleService",
	"csi/prefs",
	"csi/prefs/_base",
	"csi/printUtil",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/resolver",
	"csi/router",
	"csi/service/filterAttributeService",
	"csi/service/nodeService",
	"csi/siteDefaultLocale",
	"csi/state",
	"csi/state/rootStore",
	"csi/utility/routerUtility",
	"csi/store/Nodes",
	"csi/store/UserNames",
	"csi/types",
	"csi/util",
	"csi/utility/pathUtility",
	"csi/widget/MessageDialog",
	"csi/widget/_svg/ImpossiblePlaceholderPopup",
	"csi/widget/_svg/Plugins",
	"csi/widget/_svg/util",
	"csi/widget/SvgCanvasEditor",
	"csi/xml",
	"csi/xml/Query",
	"./_CanvasMixin",
	"./CanvasToolbar",
	"./canvasSplash",
	"dojo/text!./CanvasWidget.html",
], function(declare, baseFx, lang, domConstruct, domGeometry, all, Memory, topic, _, {CanvasMode}, csi, Deferred, csiAction, Controller, FormData,
	api, ImageReview, storeFactory, config, ExtendedPath, def, Action, Relationship, GalleryWidget, csiHeading, html, ViewRef, locale, message, ruleService,
	prefs, csiPrefs, printUtil, reflection, reflectionHints, resolver, {confirmLeavePage}, {populateFilterSpecsForTypes}, nodeService, siteDefaultLocale, state, {browserStore}, {registerNavigationCheck},
	Nodes, UserNames, types, util, pathUtility, MessageDialog, ImpossiblePlaceholderPopup, SvgPlugins, svgUtil, SvgCanvasEditor, xml, Query, _CanvasMixin, CanvasToolbar, canvasSplash, template) {

	var ACTION_ATTR_PLACEHOLDER = "CreateAttributePlaceholder";
	var ACTION_DELETE = "CanvasDeletePage";
	var ACTION_EDIT = "CanvasEdit";
	var ACTION_FROM_TEMPLATE = "CanvasFromTemplate";
	var ACTION_IMAGE = "CanvasBrowseImage";
	var ACTION_PDF = "ViewPdf";
	var ACTION_PRINT = "ViewPrint";
	var ACTION_SAVE = "CanvasSave";
	var CANVAS_URL_SUFFIX = "_CANVAS";
	var CONTROLLER_PATH = "csi/datasheet/CreateAttributePlaceholder";
	var CSS_SELECTED = "csi-selected";
	var CSS_THUMBNAIL_CONTENT = "csi-canvas-thumbnail-content";
	var CSS_THUMBNAIL_INDEX = "csi-canvas-thumbnail-index";
	var FIXED_THUMBNAIL_HEIGHT = 100;		// if landscape, then height fixed at 100, and width will vary between 130 and max
	var FIXED_THUMBNAIL_WIDTH = 128;		// if portrait, then width fixed at 128, and height will vary between 166 and 211
	var MAX_THUMBNAIL_WIDTH = 160;			// if landscape, width can vary based on aspect ratio but cannot exceed max value

	return declare([GalleryWidget, _CanvasMixin], {
		"class": "svgCanvasContainer",
		bPrintView: true,
		bResizeOnChange: true,
		disableCustomViewToolbar: true,
		disableOtherViewsWhenModal: true,
		querySize: 0,
		querySizeFixed: true,
		splitterType: "datasheet",
		templateString: template,

		plugins: [
			"fullScreen", "paperSize", "snapToGrid", "zoom",
			"edit", "editSeparator", "print", "pdf", "imageReplaceIndicator", "imageReplaceLoadingIndicator",
			"saveAndFinish", "save", "cancel", "|", "addPage", "copyPage", "|", "undo", "redo", "delete", "|", "SELECT", "line", "curve", "pen", "shapeSelect", "textArea", "image", "typePlaceholder", "imagePlaceholder", "3DBrowse", "|", "group", "ungroup", "lock", "unlock", "formatPainter",
			"composite", "canvasrightmenu"
		],

		// override default inherited from gallery widget
		pluginsRight: [],

		// connection points
		canvasAndNotesNode: null,
		canvasContainerNode: null,
		canvasWidgetNode: null,
		containerNode: null,
		focusNode: null,
		insertNode: null,
		pageNode: null,
		pageBlankNode: null,
		pageContentNode: null,
		rightNode: null,
		splashCanvasNode: null,
		splitNode: null,
		titleBlockNode: null,

		// action controllers
		_actionControllers: {
			CanvasBrowseImage: "csi/datasheet/CanvasImageController",
			CanvasBrowseImageFromPlaceholder: "csi/datasheet/CanvasImageController",
			CanvasEdit: "csi/datasheet/EditCanvasController",
			CanvasSave: "csi/datasheet/SaveCanvasController"
		},

		/** @property {Deferred} _deferredSvg - svg generating defer for all the pages */
		_deferredSvg: null,

		/** @property {VoidFunction} blurCurrentNote - there may be a current note that has not been blurred yet, call this function to blur manually */
		blurCurrentNote: null,

		/** @property {Number} canvasHeight - height of canvas based on current paper size and orientation */
		canvasHeight: 0,

		/** @property {NodesStore} canvasTemplateStore - canvas template store */
		canvasTemplateStore: null,

		/** @property {Number} canvasWidth - width of canvas based on current paper size and orientation */
		canvasWidth: 0,

		/** @property {boolean} bInitCanvas - set to true if view is being drawn the first time or being refreshed */
		bInitCanvas: false,

		/** @property {boolean} bFromMobile - indicate if belongs to mobile app */
		bFromMobile: false,

		/** @property {boolean} blockPlaceholderCheck - if true, don't check placeholder values - used on refresh from accept placeholder updates */
		// blockPlaceholderCheck: false,

		/** @property {boolean} bRememberScroll - flag indicating whether or not to remember canvas scroll position, set false to re-center canvas when _onResize is called */
		bRememberScroll: true,

		/** @property {boolean} bPrintResetCache - set to true since this view uses client nodes in the cache even after saving, which causes problems with print/pdf */
		bPrintResetCache: true,

		/** @property {Node} canvasOwner - by default, the owner is the current node. However, if on a DataSheetRevision, then the new context becomes the parent DataSheet.
		 * And finally, if that DataSheet has a parent, then the parent becomes the owner. The one case where this is not a DataSheetRevision is currently Theme. */
		canvasOwner: null,

		/** @property {boolean} finishedCanvasLoad - Set to true once postViewDeferred has been called */
		finishedCanvasLoad: false,

		/** @property {Object} mapSavedUrls - map of client urls used for new pages/comments to persisted urls on server; client urls are used, even after saving, until the view is refreshed */
		mapSavedUrls: null,

		/** @property {boolean} saved - set to true if content has just been saved; set to false once another change happens */
		saved: false,

		/** @property {Record<string, C8Node[]>} templateGridContainerMap - state map for template menu grid */
		templateGridContainerMap: null,

		/** @property {Number} thumbnailHeight - height of thumbnail based on current paper size and orientation */
		thumbnailHeight: 0,

		/** @property {Number} thumbnailWidth - width of thumbnail based on current paper size and orientation */
		thumbnailWidth: 0,

		/** @property {number} zoom - current zoom level for canvas (not persistent) */
		zoom: 1,

		// Extension point for clearing image selections
		clearImageSelections: function() { },

		// Extension point for refreshing template pages
		refreshTemplatePages: function() { },

		// Extension point for updating the image preview upload button
		updateGridRightUpload: function(fileNodes) { },

		// Used to create new canvas from template or just fresh canvas
		splashTemplate: null,

		/**
		 * if content has notes, then create new copies of them; the author and time will be updated to current user and time
		 * @param {String[]} oldNotes - array of comment url's
		 * @return {Node[]} - returns array of copied notes
		 */
		copyNotes: function(oldNotes) {
			var notes;

			if (oldNotes) {
				notes = _.map(oldNotes, function(url) {
					var note = this.cacheView.getNode(url);
					var node = this._createClientNode("Comment");
					xml.nodeCreateClientAttr(node, "Author", xml.ATTR_REF, state.get("userUrl"));
					xml.nodeCreateClientAttr(node, "Comment", xml.ATTR_STRING, note ? note.Comment : "");
					xml.nodeCreateClientAttr(node, "Date", xml.ATTR_TIME, types.getDateTime());
					return node.$URL;
				}, this);
			} else {
				notes = [];
			}

			return notes;
		},

		/**
		 * Add either a new page, or a copy of the selected page, after the selected page if under the page limit
		 * @param {CanvasContent} [content]
		 * @return {boolean} - returns false if over the page limit
		 */
		addPage: function(content) {
			if (this.hasMaxPages()) {
				svgUtil.popupMaxPageErrorMessage();
				return false;
			}

			var node = this._createClientNode("DataSheetPage");
			var notes = this.copyNotes(content ? content.notes : null);

			xml.nodeCreateClientAttr(node, xml.ATTR_PARENT, xml.ATTR_REF, this.urlRoot);
			xml.nodeCreateClientAttr(node, "Comments", xml.ATTR_REFVECTOR, notes);
			xml.nodeCreateClientAttr(node, "ContentHtml", xml.ATTR_STRING, content ? content.svg : "");
			xml.nodeCreateClientAttr(node, "ContentJson", xml.ATTR_STRING, content ? content.data : null);
			xml.nodeCreateClientAttr(node, "Keys", xml.ATTR_STRINGMAP, content ? content.keys : null);
			xml.nodeCreateClientAttr(node, "SplitPct", xml.ATTR_FLOAT, content ? content.split : -1);

			this.updateSvgStore(this.getCanvasContent(node), true);

			this.insertAndSelectPage(node);

			return true;
		},

		/**
		 * Add a page from a template after the selected page if under the page limit
		 * @param {Node} [page]
		 */
		addPageFromTemplate: async function(page) {
			const {canvasWidgetNode} = this;

			if (this.hasMaxPages()) {
				svgUtil.popupMaxPageErrorMessage();
			}

			// Auto-fill attribute placeholders that can be auto-filled.
			const result = await svgUtil.autoFillAttributePlaceholders(canvasWidgetNode, this.getViewNodeType(), [page]);
			if (result) {
				page = result[0];

				const node = this.createPageNode(page);
				this.insertAndSelectPage(node);
			}
		},

		/**
		 * Inserts the given page node into the canvas at the current position and selects it
		 * @param {Node} node
		 */
		insertAndSelectPage: function(node) {
			// insert page before next
			var refNode = this.getSelectedRefNode();
			if (refNode) {
				refNode = this.getRefNodeNext(refNode);
			}

			// add block for new temp node
			var self = this;
			var canvas = this.canvasWidgetNode;
			var ctx = this.getContext(node.$URL);

			this.when(this.addBlocks([node], refNode, true), function() {
				self.toggleBlankCanvas(false);
				self.defView.m_formData.changeSelection(ctx, true);

				if (canvas.editMode) {
					canvas.push("new", true);
					canvas.setImpossiblePaths();
					self.showImpossibleNotification();
				} else {
					self.editCanvas();
				}
			});

		},

		/**
		 * Creates a copy of the referenced page node
		 * @param {Node} page
		 * @returns {Node} page with values copied
		 */
		createPageNode: function(page) {
			// create client node for page
			var node = this._createClientNode("DataSheetPage");
			var notes = this.copyNotes(page.Comments);

			xml.nodeCreateClientAttr(node, xml.ATTR_PARENT, xml.ATTR_REF, this.urlRoot);
			xml.nodeCreateClientAttr(node, "Comments", xml.ATTR_REFVECTOR, notes);
			xml.nodeCreateClientAttr(node, "ContentJson", xml.ATTR_STRING, page.ContentJson);
			xml.nodeCreateClientAttr(node, "Keys", xml.ATTR_STRINGMAP, page.Keys);
			xml.nodeCreateClientAttr(node, "SplitPct", xml.ATTR_FLOAT, page.SplitPct);

			this.updateSvgStore(this.getCanvasContent(node), true);

			return node;
		},

		/**
		 * Add all the pages from a template node
		 * @param {Object} canvasTemplate canvas template object
		 * @param {Object[]} pages - canvas template pages
		 */
		addTemplatePages: async function(canvasTemplate, pages) {
			const {
				canvasWidgetNode,
				defView,
				toolbarNode,
			} = this;
			// if we are starting out from a template, need to toggle that template
			const addPagePlugin = toolbarNode.getPlugin("addPage");
			addPagePlugin.dropDown.toggleGridMenu(canvasTemplate);
			// Auto-fill attribute placeholders that can be auto-filled.
			const filledPages = await svgUtil.autoFillAttributePlaceholders(canvasWidgetNode, this.getViewNodeType(), pages);
			if (filledPages) {
				const pageNodes = filledPages.map(page => this.createPageNode(page));
				const ctx = this.getContext(pageNodes[0].$URL);

				await this.addBlocks(pageNodes, null, true);
				await this._populateSvg(true);

				this.toggleBlankCanvas(false);
				defView.m_formData.changeSelection(ctx, true);
				this.updateThumbnails();
				this.editCanvas();
			}
		},

		/**
		 * Return true if canvas is editable
		 * @return {boolean}
		 */
		canEditCanvas: function() {
			return xml.nodeHasAccess(this.getViewNode(), xml.RIGHT_WRITE, "Pages") && !this.defView.isReadOnly();
		},

		/**
		 * Disable scrolling of view as canvas has custom template and handles scrolling on its own
		 * @override
		 */
		canScrollAutomatically: function() {
			return false;
		},

		/**
		 * Delete the given page, or selected page if none given
		 * @param {string} [url]
		 */
		deletePage: function(url) {
			this.focus();

			const {canvasWidgetNode} = this;
			const node = this.defView.m_formData.getSelectedNode();
			let nextNodeUrl = node.$URL;

			url = url || nextNodeUrl;

			// if page being deleted is the selected one, then find next page to select
			if (url == nextNodeUrl) {
				nextNodeUrl = this._getNextPage(url);
			}

			const pageContentToDelete = this.svgStore.get(url);
			pageContentToDelete.ops = "delete";
			pageContentToDelete.sortList = this.getVisibleNodes();
			pageContentToDelete.selected = canvasWidgetNode.rememberedSelections || canvasWidgetNode.getShapeSelections();
			canvasWidgetNode.pushToUndoStack(pageContentToDelete);
			this.svgStore.remove(url);

			if (nextNodeUrl) {
				this.changeCanvasSelection(nextNodeUrl);
			} else {
				canvasWidgetNode.set("content", null);
			}

			this.updateThumbnailIndices();
			this.toggleBlankCanvas(true, url);
		},

		/**
		 * These are actions to run on canvas before going into or out of edit mode.
		 * @param {boolean} bSave - indicate if user is saving the canvas and leaving edit mode or editing the canvas going into edit mode
		 */
		async preCanvasEditOrSaveActions(bSave) {
			const {canvasWidgetNode, svgStore} = this;
			const {canvasApp} = canvasWidgetNode;
			const svgStoreNodes = await svgStore.query();
			const dataMap = svgStoreNodes.filter(node => !!node.data);
			if (dataMap.length > 0) {
				dataMap.forEach((node) => {
					node.data = canvasApp.pageHelper.setupPlaceholderLabelOptionally(node.data, !bSave);
					this.updateSvgStore(node, true);
				})
				await this.copyJSONtoSVGandUpdateThumbnails();
				this.updateCanvasContentWithCurrentPage();
			}
		},

		/**
		 * Update the current canvas content with the latest from the svgStore at the current page.
		 */
		updateCanvasContentWithCurrentPage() {
			svgUtil.setCanvasAppContent(this.canvasWidgetNode.canvasApp, this.svgStore.data.find(d => d.id === this.getSelectedUrl()));
		},

		/**
		 * Perform validations before changing the canvas into edit mode and remember selected page after data refresh
		 */
		async edit() {
			// start canvas actions that happen immediately before entering edit mode
			await this.preCanvasEditOrSaveActions();
			//this.selectedIndex = this.svgStore.index[this.getSelectedUrl()];
			const selectedPage = this.getSelectedRefNode();
			// refresh for latest data because the canvas could have been edited before the user clicks edit
			await this.refreshData();
			// when editing the canvas, it should be re-centered
			csiAction.invoke(this, selectedPage, this.ACTION_SELECT);
			this.bRememberScroll = false;
			csiAction.invoke(this, this.domNode, this.getAction(ACTION_EDIT));
		},

		/**
		 * Start editing canvas
		 */
		editCanvas: function() {
			var cache = this.cacheView;
			var canvas = this.canvasWidgetNode;
			var urls = this.getVisibleNodes();
			var obj = {};

			// store original content for each page for use in undo/redo - only need shallow copy as anytime keys or notes change,
			// the whole array will be reassigned
			this.svgStore.query().forEach(function(content) {
				obj[content.id] = _.clone(content);
			});

			canvas.originalContent = obj;
			canvas.originalSortList = urls;
			canvas.edit();
			this.showImpossibleNotification();

			// C8-14938
			// Keep Canvas Thumbnail from shrinking automatically
			// Ideally _onResize is modified so a setTimeout is not used here,
			// but for now the timeout is needed so that the canvas is shown (from the
			// _onResize function) before we update the splitter position.
			util.wait(200).then(() => {
				this._updateSplitterOnEdit(true);
			});
		},

		/**
		 * Transition from edit mode to view mode
		 * @param {boolean} bSaved
		 */
		exitEditMode: function(bSaved) {
			const {canvasWidgetNode} = this;
			this.hideImpossibleNotification();
			if (canvasWidgetNode.editMode) {
				this._updateSplitterOnEdit(false);
				this.toggleBlankCanvas(false);
				this._showSplash(!this.hasPage());
				canvasWidgetNode.exitEditMode();

				if (!bSaved) {
					this.bInitCanvas = true;
					this.redrawNewData();
				} else {
					// re-render notes node in order to redraw them as disabled
					canvasWidgetNode.notesNode.redraw();
				}

				this.set("saved", true);
				state.set("modalView", null);

				// reset the flag
				this.bFromMobile = false;

				// exit fullscreen mode when leaving edit mode
				browserStore.exitFullScreen();
			}
		},

		/**
		 * Focus on hidden input field to keep focus off of toolbar widgets
		 */
		focus: function() {
			html.focus(this.focusNode);
		},

		/**
		 * Return object with current size of canvas in pixels
		 * @return {Object}
		 */
		getCanvasBox: function() {
			return this.canvasWidgetNode ? domGeometry.getContentBox(this.canvasWidgetNode.canvasNode) : {w: 0, h: 0};
		},

		/**
		 * Return layout with titleblock height subtracted from page height
		 * @return {Object}
		 */
		getCanvasPageLayoutMinusTitleblock: function() {
			var layout = this.getCanvasPageLayout();
			layout.h -= $(this.titleBlockNode).height();
			return layout;
		},

		/**
		 * Return true if the number of pages is at the maximum
		 * @return {boolean}
		 */
		hasMaxPages: function() {
			return this.svgStore && (this.svgStore.query().total == api.getCanvasPageLimit());
		},

		/**
		 * Return true if there is at least one page
		 * @return {boolean}
		 */
		hasPage: function() {
			return this.svgStore && !!this.svgStore.query().total;
		},

		imageReview: function() {
			const action = new ImageReview();
			action.resolve();
			csiAction.invoke(this, this.domNode, action);
		},

		/**
		 * Invoke action controller with given id and params to show add attribute placeholder dialog
		 * @param {Object} params
		 */
		insertAttributePlaceholder: function(params) {
			csiAction.invoke(this, this.domNode, this.getAction(ACTION_ATTR_PLACEHOLDER), params);
		},

		/**
		 * Invoke action controller with given id and params to show add image dialog
		 * @param {Object} params
		 */
		insertImage: function(params) {
			csiAction.invoke(this, this.domNode, this.getAction(ACTION_IMAGE), params);
		},

		/**
		 * Show dialog used to add image placeholder
		 * @param {Object or Object[]} placeholderList - list of placeholders or single placeholder
		 */
		insertImagePlaceholder: function(placeholderList) {
			csiAction.invoke(this, this.domNode, this.getAction(ACTION_IMAGE), {placeholder: placeholderList});
		},

		/**
		 * Return false if node is not Datasheet or descendant thereof.
		 * @return {boolean}
		 */
		isDataSheetCanvas: function() {
			return this.defView.getRootClass().isDerivedFrom("DataSheetRevision");
		},

		/**
		 * Return true if there are unsaved changes to canvas
		 * Also, return true if the c8 client side take over the canvas from the mobile apps
		 */
		isDirty: function() {
			return (!this.saved && this.canvasWidgetNode && this.canvasWidgetNode.isCanvasDirty()) || this.bFromMobile;
		},

		/**
		 * @override
		 * Canvas has custom toolbar, so override initialization of toolbar
		 */
		loadPlugins: function() {
			var toolbarNode = this.toolbarNode;

			// takes place of setView
			toolbarNode.view = this;

			var canvas = this.canvasWidgetNode;
			if (canvas) {
				_.each(this.plugins, function(p) {
					canvas.addPlugin(p, toolbarNode);
				});
			}
		},

		/**
		 * Print canvas content
		 * @param {boolean} bPreview
		 */
		print: function(bPreview) {
			csiAction.invoke(this, this.domNode, this.getAction(ACTION_PRINT), {bPrintPreview: bPreview});
		},

		/**
		 * Export canvas content as PDF
		 */
		printToPdf: function() {
			csiAction.invoke(this, this.domNode, this.getAction(ACTION_PDF));
		},

		/**
		 * Save canvas content, with option to finish editing on success
		 * @param {boolean} bFinish
		 * @param {boolean} bReviewUpdates - if we are reviewing updates, this is true
		 */
		async save(bFinish, bReviewUpdates) {
			// start canvas actions that happen immediately before saving
			await this.preCanvasEditOrSaveActions(true);

			if (bFinish && !bReviewUpdates) {
				const plugin = this.canvasWidgetNode.getPlugin("canvasrightmenu");
				plugin.cancelStoreRequests();
			}

			csiAction.invoke(this, this.domNode, this.getAction(ACTION_SAVE), {
				bFinish,
				bReviewUpdates,
				m_urlParent: this.urlRoot,
				m_idAttrParent: "Pages",
			});
		},

		/**
		 * Regenerate canvas snapshots.
		 * @param {Function} [callback] - optional function to call after canvas regenerates snapshots
		 */
		regenerateSnapshots(callback) {
			csiAction.invoke(this, this.domNode, this.getAction(ACTION_SAVE), {
				bRegenerateSnapshots: true,
				m_urlParent: this.urlRoot,
				m_idAttrParent: "Pages",
				onPublishResponse: (response) => {
					callback();
				}
			});
		},

		/**
		 * Select next or previous page
		 * @param {boolean} bPrevious
		 */
		selectNextPage: function(bPrevious) {
			var page = this.getSelectedRefNode();
			if (page) {
				do {
					page = bPrevious ? page.previousSibling : page.nextSibling;
				} while (page && !html.isVisible(page));

				if (page) {
					csiAction.invoke(this, page, this.ACTION_SELECT);
				}
			}
		},

		/**
		 * Render gallery thumbnails as normal and reset canvas page view
		 */
		_buildView: function() {
			// determine canvas owner that is used for images and possibly when saving - this must be done after dependency paths are queried
			if (!this.canvasOwner) {
				var node = this.getViewNode();
				var rc = reflection.getNodeClass(node);

				if (rc.isDerivedFrom("DataSheetRevision")) {
					var dataSheet = this.cacheView.getNode(node.__Parent__);
					var dataSheetContainer = this.cacheView.getNode(dataSheet.__Parent__);
					node = dataSheetContainer || dataSheet;
				}

				this.canvasOwner = node;
			}

			this.defView.m_formData.resetSelections(null, true);
			this.cacheView.removeClientNodes(CANVAS_URL_SUFFIX);
			this.mapSavedUrls = {};
			this.inherited(arguments);
		},

		/**
		 * notify switchboard of query results
		 * @param {QueryResults} results
		 *  @param {boolean} bRefresh
		 */
		_buildViewResults: function(results, bRefresh) {
			// migrate DataSheetPage nodes if necessary
			this.migrateCanvasSplits(results);

			var ctx = this.getContext(csi.EMPTY_URL);
			if (ctx.bPrint) {
				// remove contents of domNode
				this._destroyToolbar();
				html.clear(this.domNode, true);

				// populate all pages
				return this.renderCanvasPages(ctx, results, this.domNode, !this.isProductPresentationLayout());
			}

			var canvasPages = _.map(results, this.getCanvasContent);
			this.svgStore = new Memory({data: canvasPages});
			this._deferredSvg = this._populateSvg(false);

			return this.inherited(arguments);
		},

		/**
		 * Creates client node in cache of given type
		 * @param {string} type
		 * @return {Node}
		 */
		_createClientNode: function(type) {
			return this.cacheView.createClientNode(xml.getClientNodeUrl(CANVAS_URL_SUFFIX), type);
		},

		/**
		 * Execute select view after finish dragging the drag node
		 * @param {Source} target
		 * @param {Source} source
		 * @param {Node[]} node
		 * @param {boolean} copy
		 */
		_dndDrop: function(target, source, nodes, copy) {
			this.updateThumbnails();
			this.canvasWidgetNode.push("dnd", true);
		},

		/**
		 * Return action node for given block
		 * @param {DOMNode} refNode
		 */
		_getBlockActionNode: function(refNode) {
			return html.queryDom([html.getDataAttributeSelector(html.DATA_TEMPLATE_ACTIONS, "1")], refNode, true);
		},

		/**
		 * Only render delete action; actions may be null when canvas is not editable
		 */
		_getBlockActions: function() {
			var actions = this.getMainQueryLevel().actions;
			if (actions) {
				actions.m_actions = [this.getAction(ACTION_DELETE)];
			}
			return actions;
		},

		/**
		 * Get next visible page; if next page is not visible, then return the previous page that is visible (if any)
		 */
		_getNextPage: function(url) {
			var sortOrderList = this.getVisibleNodes();
			var currentIndex = _.indexOf(sortOrderList, url);
			return sortOrderList[currentIndex + 1] || sortOrderList[currentIndex - 1];
		},

		/**
		 * Return zoom value to be applied to thumbnail content
		 * @return {number}
		 */
		_getThumbnailZoom: function() {
			const {splitNode} = this;
			if (splitNode) {
				const max = splitNode.get("maxPixels1");
				const fixed = max - MAX_THUMBNAIL_WIDTH; // fixed is size of pane that is not part of thumbnail
				return (splitNode.pane1.offsetWidth - fixed) / (max - fixed);
			} else {
				return 1;
			}
		},

		/**
		 * Load selected custom view
		 */
		_onChangePdfCustomView: function() {
			this.refreshDefaultCustomView();

			// now that the custom view settings were changed, close the Canvas Template grid menu
			this.getPlugin("addPage").dropDown.toggleGridMenu();
		},

		/**
		 * React to change in split pane between thumbnails and canvas
		 * @param {number} value
		 * @param {boolean} dragging
		 */
		_onChangeThumbnailSplit: function(value, dragging) {
			if (dragging) {
				$(this.splitNode.pane1).find("." + CSS_THUMBNAIL_CONTENT).css("zoom", this._getThumbnailZoom());
			}
		},

		/**
		 * Insert new page when canvas is empty
		 */
		_onClickBlankContainer: function() {
			this.addPage();
		},

		/**
		 * Select a canvas template to start from
		 */
		_onClickTemplateContainer: function() {
			csiAction.invoke(this, this.domNode, this.getAction(ACTION_FROM_TEMPLATE));
		},

		/**
		 * Keep height of thumbnail pane in sync with height of canvas
		 */
		_onResize: function() {
			if (state.get("print")) {
				return;
			}

			// if view is not on selected tab, then wait to resize until tab is selected
			if (!this.domNode.offsetHeight) {
				return;
			}

			if (this.finishedCanvasLoad) {

				const {
					blockNode,
					canvasContainerNode,
					canvasWidgetNode,
					insertNode,
					pageBlankNode,
					pageContentNode,
					splitNode,
				} = this;

				// get page helper
				const {pageHelper} = canvasWidgetNode.canvasApp;

				// remember current scroll position if we are not initializing canvas and the remember scroll flag is true, otherwise canvas will re-center
				let prevScrollPosition = false;
				if (this.bRememberScroll && !this.bInitCanvas) {
					prevScrollPosition = pageHelper.getCanvasContainerScrollPosition();
				}

				// reset scroll flag
				this.bRememberScroll = true;

				// resize the splitter allowing user to hide thumbnail panel
				splitNode.resize();


				// if changing from splash screen to canvas, then change container display so that it can be sized correctly, but fade in once moved to center
				if (this.hasPage() && !html.isVisible(this.canvasContainerNode)) {
					$(insertNode).css("opacity", 0);
					this._showSplash(false);
					this.bInitCanvas = true;
				}

				// set size of the canvas based on print settings
				if (this.bInitCanvas) {
					// update size of thumbnails - update template node and all current pages
					const layout = this.getCanvasPageLayoutMinusTitleblock();
					const aspectRatio = layout.w / layout.h;

					if (layout.portrait) {
						this.thumbnailWidth = FIXED_THUMBNAIL_WIDTH;
					} else {
						this.thumbnailWidth = Math.min(FIXED_THUMBNAIL_HEIGHT * aspectRatio, MAX_THUMBNAIL_WIDTH);
					}

					this.thumbnailHeight = this.thumbnailWidth / aspectRatio;

					this.canvasHeight = layout.h;
					this.canvasWidth = layout.w;

					$(blockNode).find("." + CSS_THUMBNAIL_CONTENT).css("height", this.thumbnailHeight).css("width", this.thumbnailWidth);
					$(this.getContentRoot()).find("." + CSS_THUMBNAIL_CONTENT).css("height", this.thumbnailHeight).css("width", this.thumbnailWidth);

					// update size and orientation of canvas edit area - add splitter size (7) + 1 pixel to account for the splitter while editing (as opposed to
					// size for print or thumbnail), as well as the 1 pixel that is subtracted in getCanvasPageLayout to account for border between canvas and notes
					const SPLITTER_SIZE = 7 + 1;
					const editHeight = layout.h + (layout.vertical ? SPLITTER_SIZE : 0);
					const editWidth = layout.w + (layout.vertical ? 0 : SPLITTER_SIZE);

					$(pageBlankNode).width(editWidth);
					$(pageContentNode).width(editWidth);
					$(canvasWidgetNode.domNode).height(editHeight);
					canvasWidgetNode.set("vertical", layout.vertical);
				}

				// determine actual zoom ratio for Fit to Screen (0)
				const pageSize = this.getCanvasPageLayout();

				// set view height and width manually instead of getting them from DOM
				let transform = "";

				let scale = pageSize.w ? this.zoom : 1;

				// fit to screen - consider both height and width to come to a decision
				if (!scale) {
					// get bound of first canvas toolbar (top one)
					const upperToolbarBound = canvasContainerNode.parentElement.querySelector(".csiViewToolbar").getBoundingClientRect();

					// bottom of the first canvas toolbar gets top
					const top = upperToolbarBound.bottom;

					// use the toolbar for the right bound, and if we're in edit mode, account for right side menu, which is 261px
					const right = canvasWidgetNode.editMode ? upperToolbarBound.right - 261 : upperToolbarBound.right;

					// bottom of the canvas container node gets bottom
					const bottom = canvasContainerNode.getBoundingClientRect().bottom;

					// use the right side of the left splitter in order to get the correct position of the left side canvas bound
					const left = canvasContainerNode.querySelector(".csi-split-pane-splitter").getBoundingClientRect().right;

					// subtract 0.07 from scale as to add 7% padding
					scale = Math.min((((right - left) / pageSize.w) - 0.07), (((bottom - top) / pageSize.h) - 0.07));
				}

				if (scale != 1) {
					transform = "scale(" + scale + ")";

					// the view's DOM width does not change with scale, so do so manually
					pageSize.w *= scale;
				}

				// this transform will require a relative position
				$(insertNode).css("transform", transform);

				const blankPageBound = insertNode.querySelector(".csi-canvas-blank-container").getBoundingClientRect();
				const canvasPageBound = pageContentNode.getBoundingClientRect();
				const scrollNodeBound = canvasContainerNode.getBoundingClientRect();

				// when resizing, pre-set padding based on scale so calculations of contentRect are correct
				// top starts at 10px, left starts at amount it takes to center canvas
				insertNode.style.paddingLeft = ((scrollNodeBound.width - (canvasPageBound.width || blankPageBound.width)) / 2 * (1 / scale)) + "px";
				insertNode.style.paddingTop = "10px";

				// pass zoom factor onto canvas widget
				canvasWidgetNode.set("zoom", scale);

				// finish initializing canvas
				if (this.bInitCanvas) {
					html.setVisibility(this.domNode, true);
					this.bInitCanvas = false;
					this.updateThumbnails();
				}

				this.updatePaneHeight();

				// reset editor dimensions
				const contentRect = pageHelper.getEditorDimensions();
				pageHelper.expandEditor(contentRect, true, canvasWidgetNode.editMode, scale, prevScrollPosition);

				// fade in if not already done
				baseFx.fadeIn({node: insertNode}).play();
			} else {
				html.setVisibility(this.domNode, true);
			}

			this.inherited(arguments);
		},

		/**
		 * Return true if canvas notes is overflowing the container
		 * @return {boolean}
		 */
		isCanvasNotesOverflow: function() {
			var jCanvasAndNotes = $(this.canvasAndNotesNode);
			return jCanvasAndNotes.find(".csi-canvas-notes").height() > jCanvasAndNotes.height();
		},


		/**
		 * Customize DndSource so that slides can be dropped anywhere in left pane
		 */
		onCreateDndSource: function(source) {
			source.enableDropOverNone = true;
		},

		/**
		 * update visiblity of contents when switched between canvas
		 * @param {Context} ctx
		 * @param {string} url
		 * @param {boolean} bOn
		 * @param {boolean} bReplaced
		 */
		onSelect: function(ctx, url, bOn, bReplaced) {
			if (bOn) {
				// load content view based on selected node
				var refNode = this.getRefNodeByUrl(url);
				$(this.fadeNode).find('.' + CSS_SELECTED).removeClass(CSS_SELECTED);
				this.scrollIntoView(refNode);
				this.canvasWidgetNode.canvasApp.canvasApi().setCanvasMode(CanvasMode.SELECT);
				this.canvasWidgetNode.set("content", this.svgStore.get(url));
				$(refNode).addClass(CSS_SELECTED);
				html.focus(refNode);
			}
		},

		/**
		 * Focus element when selected
		 * @param {DOMNode} refNode
		 * @param {boolean} bSelect
		 */
		toggleRefNodeSelection: function(refNode, bSelect) {
			// when a page is re-selected or another page selected, canvas should be re-centered
			this.bRememberScroll = false;
			// this is necessary so that node is focused when re-selected (already selected, but not focused)
			html.focus(refNode);

			// there might be a current note
			this.blurCurrentNote?.();

			this.inherited(arguments);
		},

		/**
		 * Remove placeholders containing inaccessible types from all canvas pages
		 */
		unlinkAllAttributePlaceholders: function() {
			this.svgStore.query().forEach((item) => {
				const newData = JSON.parse(item.data);
				newData.pageObjects = this.canvasWidgetNode.canvasApp.pageHelper.unlinkAllAttributePlaceholders(newData.pageObjects);
				item.data = JSON.stringify(newData);
			});
			svgUtil.setCanvasAppContent(this.canvasWidgetNode.canvasApp, this.svgStore.data[0]);
			this.canvasWidgetNode.placeholderData = null;
			this.canvasWidgetNode.placeholderDataChanged = false;
			this.canvasWidgetNode.placeholdersModified = true;
			this.save(true, true);
		},

		/**
		 * Populate the svg per App
		 * @param {boolean} fromCanvasTemplate - true if canvas is being rendered from template
		 * @return {Deferred}
		 */
		_populateSvg: function(fromCanvasTemplate) {
			var self = this;
			var contents = this.svgStore.query();
			var pageMap = {};
			var svgDfds = [];

			function onContentChange(p) {
				var id = p.pageId;
				var pageData = pageMap[id];
				if (pageData) {
					// update the svg for pageId in svgStore.
					var app = pageData.app;
					var content = pageData.content;
					var dfd = pageData.dfd;

					pageMap[id] = null;
					svgUtil.removeCanvasApp(app);
					content.svg = svgUtil.resolveCanvasAppImageHref(p.pageHtml);
					self.svgStore.put(content);
					dfd.resolve();
				}
			}

			var listeners = {
				onContentChange: onContentChange
			};

			_.each(contents, function(content) {
				if (content.svg) {
					return;
				}

				// Creating c8-canvas app instance to generate svg's
				var dfd = new Deferred();
				var params = {
					editorContainer: document.createElement("div"),
					isPrimaryInstance: false,
					dontDownloadImages: true
				};
				var canvasApp = svgUtil.createCanvasApp(params, listeners);
				canvasApp.canvasApi().setIsCanvasTemplate(self.isCanvasTemplate());

				pageMap[content.id] = {
					app: canvasApp,
					content: content,
					dfd: dfd
				};

				svgUtil.setCanvasAppContent(canvasApp, content);

				// setup placeholders during load for canvas instances created before the stories and versions mentioned below
				// C8-16890 7.2
				// C8-49701 7.7
				content.data = canvasApp.pageHelper.setupPageContentPlaceholderHTML(content.data, fromCanvasTemplate);

				svgDfds.push(dfd);
			});

			// Check if attribute placeholders data values have changed,
			// and if placeholder attributes are invalid and should be deleted.
			// Don't run review updates on:
			// 1. Canvas Templates
			// 2. BOM Templates
			// 3. Size Chart Templates
			// 4. Style Templates
			const {canvasOwner} = this;
			const {$Type} = canvasOwner;
			const isTemplateNode = reflectionHints.isTemplateNode(canvasOwner);
			const isBomTemplate = $Type === "ApparelBOM" && isTemplateNode;
			const isSizeChartTemplate = $Type === "SizeChart" && isTemplateNode;
			const isStyleTemplate = $Type === "Style" && isTemplateNode;
			if (!fromCanvasTemplate && !isBomTemplate && !isSizeChartTemplate && !isStyleTemplate) {
				if (this.blockPlaceholderCheck) {
					this.blockPlaceholderCheck = false;
				} else {
					svgDfds.push(self.canvasWidgetNode._checkPlaceholderValues(contents));
				}

			}

			return all(svgDfds);
		},

		/**
		 * Re-create content view, unless printing where views are inserted in _buildViewResults
		 * @param {Context} ctx
		 * @param {Deferred[]} dfds
		 */
		_postViewDeferred: function(ctx, dfds) {
			if (ctx.bPrint) {
				this.postRenderCanvasPages();
			} else {
				var item = this.svgStore.query()[0];

				// hide content until it is resized if first loading view or refreshing
				var self = this;
				if (!this.viewLayout) {
					$(this.insertNode).css("opacity", 0);

					const defView = def.getNodeViewDef(this.getViewNode(), "TitleBlock");
					if (defView) {
						var dfd = this._insertAndCreateView(defView, this.getViewNode(), this.titleBlockNode).then((view) => {
							self.viewLayout = view;
							self.bInitCanvas = true;
							self.finishedCanvasLoad = true;
						});
						dfds.push(dfd);
					} else {
						this.viewLayout = null;
						this.bInitCanvas = true;
						this.finishedCanvasLoad = true;
					}
				}

				// update visibility of view components
				$(this.containerNode).toggleClass("csi-canvas-no-edit", !this.canEditCanvas());
				this.toggleBlankCanvas(false);
				this.canvasWidgetNode.updateToolbar();
				this._showSplash(!item);

				// select desired page, or first if none given
				if (item) {
					this.changeCanvasSelection(item.id);
				}

				// update the toolbar and thumbnails
				this.when(this._deferredSvg, function() {
					self.canvasWidgetNode.updateToolbar();
					self.updateThumbnails();
				});
			}
		},

		/**
		 * Set zoom factor for canvas and resize, which is where all the work will be done
		 * @param {number} value
		 */
		_setZoomAttr: function(value) {
			// canvas should be re-centered when zoom is changed
			this.bRememberScroll = false;
			this._set("zoom", value);
			this.resize();
		},

		/**
		 * Change visibility of splash page
		 * @param {boolean} bShow
		 */
		_showSplash: function(bShow) {
			html.setVisible(this.canvasContainerNode, !bShow);
			html.setVisible(this.splashCanvasNode, bShow);
			html.setVisible(this.toolbarNode, !bShow);
			this.splashTemplate?.set("disabled", !bShow);
		},

		/**
		 * Print CSS not needed
		 * @param {string} [paper]
		 * @param {string} [orientation]
		 * @param {DOMNode} [domNode]
		 */
		applyPrintCSS: function(paper, orientation, domNode) {
		},

		/**
		 * Add splash node into canvas page
		 */
		buildRendering: function() {
			this.inherited(arguments);

			// splash screen and navigation event handling not needed when printing
			if (state.get("print")) {
				return;
			}

			var isTemplate = this.isCanvasTemplate();

			this.splashTemplate = new canvasSplash({
				isTemplate: isTemplate
			});

			const {splashTemplate} = this;
			const {canvasCreateBtn, canvasTemplateBtn} = splashTemplate;

			canvasCreateBtn.onClick = () => {
				splashTemplate.set("disabled", true);
				this._onClickBlankContainer();
			};

			canvasTemplateBtn.onClick = () => {
				this._onClickTemplateContainer();
			};

			domConstruct.place(this.splashTemplate.domNode, this.splashCanvasNode);

			// if unsaved canvas then stop page nav on link click or tab click
			this.own(
				registerNavigationCheck(async (isPageNavigation, isTabNavigation) => {
					if (this.isDirty()) {
						try {
							await confirmLeavePage();
						} catch (err) {
							return false;
						}
					}

					// it could be in edit mode without any changes, so cancel just in case
					if (this.isValidView()) {
						this.exitEditMode();
					}

					return true;
				})
			);
		},

		/**
		 * ChangeCanvasSelection
		 *
		 * Don't fire onSelect when bQuiet is true.
		 * @param {string} nodeUrl
		 * @param {boolean} bQuiet
		 */
		changeCanvasSelection: function(nodeUrl, bQuiet) {
			this.defView.m_formData.changeSelection(this.getContext(nodeUrl), true, bQuiet);
		},

		/**
		 * @override
		 * When view is forced to be read-only, need to update the toolbar; the canvas toolbar is not enabled/disabled as
		 * other views when the view is enabled/disabled
		 */
		forceReadOnly() {
			// NOTE: do not call inherited as gallery views will redraw
			this.defView.setReadOnly(true);
			this.canvasWidgetNode.updateToolbar();
		},

		/**
		 * Given a DataSheetPage node, return item used to store all editable content
		 * @param {Node} node
		 * @return {CanvasContent}
		 */
		getCanvasContent: function(node) {
			let contentJson = node.ContentJson;
			try {
				JSON.parse(contentJson);
			} catch (err) {
				contentJson = svgUtil.generateEmptyPageJson(node.$URL);
			}
			return {
				data: contentJson,
				id: node.$URL,
				keys: node.Keys,
				notes: node.Comments,
				split: node.SplitPct,
				svg: node.ContentHtml
			};
		},

		/**
		 * Returns the DOMNode for the selected thumbnail
		 * @return {DOMNode}
		 */
		getSelectedRefNode: function() {
			var url = this.getSelectedUrl();
			return url ? this.getRefNodeByUrl(url) : null;
		},

		/**
		 * Returns the DOMNode for the selected thumbnail
		 * @return {DOMNode}
		 */
		getSelectedUrl: function() {
			var node = this.defView.m_formData.getSelectedNode();
			return node ? node.$URL : null;
		},

		/**
		 * Returns the list of page urls currently in view and updates their displayed index
		 * @param {string} url
		 * @return {string[]}
		 */
		getVisibleNodes: function() {
			var visibleNodes = [];

			_.each(this.getRefNodes(), function(refNode) {
				var url = this.getRefNodeUrl(refNode);

				if (html.isVisible(refNode)) {
					visibleNodes.push(url);
				}
			}, this);

			return visibleNodes;
		},

		/**
		 * Disable drag and drop if canvas is not in editMode
		 */
		isDndAllowed: function() {
			return this.canvasWidgetNode.editMode;
		},

		/**
		 * Resize on tab switch so SplitPane will be sized correctly
		 */
		onTabSwitch: function() {
			this.updateThumbnails();
		},

		hideImpossibleNotification: function() {
			html.setVisible(this.notificationNode, false);
		},

		showImpossibleNotification: function() {
			var keys = this.canvasWidgetNode.getImpossiblePaths();
			if (keys.length) {
				html.setVisible(this.notificationNode, true);
			}
		},

		/**
		 * @override
		 * Force Edit and Save actions to be disabled if dormant location
		 */
		onViewDisable: function(disabled) {
			const {splashTemplate} = this;

			if (splashTemplate) {
				splashTemplate.set("disabled", disabled);
			}

			this.inherited(arguments);
		},

		onViewStart: function(dfds) {
			const types = [...storeFactory.getAllTargetTypes(), ...storeFactory.getReferenceDataTypes("ProductPresentationLayout")];
			dfds.push(populateFilterSpecsForTypes(types));
			dfds.push(siteDefaultLocale.initialize());
			dfds.push(UserNames.load());
			this.inherited(arguments);
		},

		getContextForPlaceholders: function() {
			var ctx = this.getContext();
			ctx.locale = state.get("siteLocale");
			return ctx;
		},

		getColorMatrixPath: function(typeProduct) {
			try {
				return ExtendedPath.create(typeProduct, pathUtility.pathCreate("ProductColors"));
			} catch (ex) {
				return;
			}
		},

		generateLayoutRulesQuery: async function() {
			const node = this.getViewNode();
			if (node.$Type !== "ProductPresentationLayout") {
				return;
			}
			const presentation = this.cacheView.getNode(node.__Parent__);
			if (!presentation) {
				return;
			}
			const presentationType = xml.enumGetText(presentation.ProductType);
			const colorMatrixPath = this.getColorMatrixPath(presentationType);    // colorMatrixPath is null for Food presentations
			const ruleNode = await nodeService.resolveNode(presentation.Rule);
			const runtimeRules = ruleNode ? ruleService.getRuntimeRulesFromNode(ruleNode, colorMatrixPath) : null;
			this.colorQuery = ruleService.getRuntimeRulesQuery(runtimeRules, colorMatrixPath);
		},

		/**
		 * Setup form data to allow node selection.
		 */
		postCreate: function() {
			const node = this.getViewNode();
			if (!node) {
				return;
			}

			var self = this;
			this.hideImpossibleNotification();
			this.generateLayoutRulesQuery();
			var controller = new Controller({
				m_action: new Action(),
				m_element: this.domNode,
				widget: this
			});

			var fd = lang.mixin(new FormData(controller), {
				m_bAllowSelections: true,
				m_bEditable: true,
				m_bSelectOne: true,
				m_bRequireOne: true
			});

			this.defView.setFormData(fd);
			this.splitNode.set("frozen", false);

			// hide content until it is first resized
			if (!state.get("print")) {
				html.setVisibility(this.domNode, false);

				this.notificationPopup = new ImpossiblePlaceholderPopup({
					ignoreClick: function() {
						self.hideImpossibleNotification();
					},
					removeClick: function() {
						self.canvasWidgetNode.removeImpossiblePaths();
						self.hideImpossibleNotification();
					}
				}).placeAt(this.notificationNode);

				// create widget for canvas and notes
				this.canvasWidgetNode = new SvgCanvasEditor({
					view: this,
					vertical: this.hasVerticalOrientation(),
					insertNode: this.insertNode,
					onChange: function() {
						self.set("saved", false);
						state.set("modalView", self.canvasWidgetNode.editMode ? self : null);

						// reset last message when user makes change
						message.hide();

						// enable vertical scroll bar when notes overflow
						if (self.isCanvasNotesOverflow()) {
							$(self.insertNode).removeClass("csi-no-scroll csi-no-vertical-scroll");
						}
					},
					onError: function(err, params) {
						message.errorDialog({message: locale.resource(err, params)});
					}
				}).placeAt(this.canvasAndNotesNode);
				this.canvasWidgetNode.startup();
			}

			// Subscribe to changes in selected attribute placeholder
			this.own(topic.subscribe("/csi/canvas/displayCustomView", (params) => this.insertAttributePlaceholder(params)));

			this.inherited(arguments);
		},

		generateTemplateQuery: function() {
			var canvasTemplateDatasheetType = "CanvasTemplateDatasheetType";
			var eq = xml.OP_EQUALS;
			var node = this.getViewNode();
			var rc = reflection.getNodeClass(node);
			var templateType;

			if (node.$Type !== "CanvasTemplate") {
				if (rc.isDerivedFrom("DataSheetRevision")) {
					// if a datasheet, then filter by data sheet type and template (datasheet parent) type
					var dataSheet = resolver.resolveNode(node.__Parent__, false, this.cacheView);
					templateType = dataSheet.$Type;
				} else {
					// if not a datasheet, then just filter by template type
					templateType = node.$Type;
				}
			} else {
				// if a CanvasTemplate, then filter for other templates with same types
				templateType = xml.enumGetText(node.Type);
			}

			// always allow templates with no template type
			var query = new Query();
			var qType = query.Or()
				.Attr("DatasheetType", eq, xml.enumCreate(canvasTemplateDatasheetType))
				.And();

			// query by template type if known (could be on a data sheet template where template type is unknown)
			if (templateType) {
				// query by subtype if template type is SpecificationDataSheet
				if (templateType == "SpecificationDataSheet") {
					qType.RefAttr("DatasheetSubtype", eq, dataSheet.Subtype);
				} else {
					qType.Attr("DatasheetType", eq, xml.enumCreate(canvasTemplateDatasheetType, templateType));
				}
			}

			// query by custom view options
			var printPaper = this.getPrintPaper();
			var validPapers = printUtil.getSmallerPaperSizes(printPaper);
			var printQor = query.Or();
			_.each(validPapers, function(p) {
				printQor.Attr("PaperSize", eq, xml.enumCreate("PaperSize", p));
			});
			var printOrientation = xml.enumCreate("PaperOrientation", this.getPrintOrientation());
			query.Attr("PaperOrientation", eq, printOrientation);

			var notesOrientation = xml.enumCreate("CanvasNotesOrientation", this.getCanvasCommentsOrientation());
			query.Attr("NotesOrientation", eq, notesOrientation);

			// only allow active templates with pages
			query.Attr("Active", eq, true);
			query.Attr("Pages", xml.OP_NOT_NULL);

			return query;
		},

		/**
		 * Initialize view properties
		 */
		postMixInProperties: function() {
			// summary: pre-process toolbar plugins
			var self = this;
			var defView = this.defView;
			var node = this.getViewNode();

			// setup resources for view
			defView.defaultImageSize = reflection.IMAGE_SIZE_SMALLEST;
			defView.templatePath = "SiteAdmin/html/DataSheetPageCard.html";
			defView.m_relationship = lang.mixin(new Relationship(), {m_id: "Pages"});

			defView.m_depPaths = [
				"Child:__Parent__/Child:__Parent__",
				"Child:Pages/Child:Comments/Child:Author",
				"Child:Pages/Child:Snapshot"
			];

			// setup actions for view
			defView._addViewAction(ACTION_DELETE, {
				materialIcon: "close",
				m_class: "csi-icon-delete",
				m_typeControl: def.ACTION_ICON,
				m_postAction: function(ac) {
					self.deletePage(ac.m_urlNode);
				}
			});

			defView._addViewAction(ACTION_EDIT, {
				confirmAction: MessageDialog.BUTTON_PROCEED
			});

			defView._addViewAction(ACTION_IMAGE);
			defView._addViewAction(ACTION_SAVE);
			defView._addViewAction(ACTION_PRINT);
			defView._addViewAction(ACTION_PDF);

			if (node) {
				defView._addViewAction(ACTION_ATTR_PLACEHOLDER, {
					classController: CONTROLLER_PATH,
					classControllerParams: {
						canvasType: node.DatasheetType
					}
				});

				// track paper size and orientation from print view
				var rc = reflection.getNodeClass(node).getMasterClass();
				var cc = rc && reflection.getClassConfig(rc.getType());
				var pdfViewId = cc ? cc.getPdfViewId() : def.DEFAULT_PRINT_VIEW_ID;
				var parentView = this.viewParent;

				if (node.$Type == "CanvasTemplate" || node.$Type == "ProductPresentationLayout") {
					// Set paper size and orientation from the canvas template values.
					this.set("printPaper", xml.enumGetText(node.PaperSize));
					this.set("printOrientation", xml.enumGetText(node.PaperOrientation));
					this.set("canvasCommentsOrientation", xml.enumGetText(node.NotesOrientation));
				} else if (!parentView || (pdfViewId != parentView.defView.getId())) {
					// get CSS and custom view settings from PDF view
					var defPrintView = def.getNodeViewDef(node, pdfViewId);
					var prefContext;

					// TODO: it would be nice to have a better way to detect this (from model?), but for now it works to detect the Spec Data Sheet subtype
					if (defPrintView.classController === "SiteAdmin/SubtypedDataSheet") {
						var subtypedNode = resolver.resolveSubtypedNode(node, this.cacheView);
						if (subtypedNode) {
							prefContext = config.getSubtypeName(subtypedNode.Subtype);
						}
					}

					this.prefKey = defPrintView.getKey(prefContext);

					// need to listed for changes to default custom view for PDF view
					this.own(prefs.watchDefaultView(this.prefKey, lang.hitch(this, this._onChangePdfCustomView)));
				} else {
					// just inherit settings from parent view - in this case the canvas is displayed under the view that owns the print settings,
					// and will be recreated any time the print settings change
					this.set("printPaper", parentView.get("printPaper"));
					this.set("printOrientation", parentView.get("printOrientation"));
					this.set("canvasCommentsOrientation", parentView.get("canvasCommentsOrientation"));
				}
			}

			this.inherited(arguments);
		},

		/**
		 * Query Canvas Template pages with snapshots and comments.
		 * @param {C8Node} template 
		 * @returns {NodeStore | null}
		 */
		getPagesStore(template) {
			return template ? new Nodes({
				cachingStore: this.cacheView,
				requestParameters: {
					"Dep.Path": ["Child:Snapshot;Comments"],
					"Ext.File.Info": true,
					"Qry.Limit.Path": xml.getUrlPath(template.$URL, "Child:Pages"),
				},
			}) : null;
		},

		/**
		 * @override
		 * Add the view action here when all the view preferences have already been loaded
		 */
		_postView: function() {
			this.inherited(arguments);

			// reset canvas template map
			this.templateGridContainerMap = {};

			// generate the canvas template query
			const canvasTemplateQuery = this.generateTemplateQuery();

			// If there is no view parent, these settings will come from Company Defaults.
			// To prevent this, set these three details from the selected custom view.
			// We couldn't do this inside postMixInProperties, because the custom view store was not loaded yet.
			if (!this.viewParent) {
				const {values: {canvasCommentsOrientation, printOrientation, printPaper}} = this.getSelectedCustomView();
				this.set("printPaper", printPaper);
				this.set("printOrientation", printOrientation);
				this.set("canvasCommentsOrientation", canvasCommentsOrientation);
			}

			// setup the Canvas Template store
			this.canvasTemplateStore = new Nodes({
				sortQuery: new Query().SortByNode(xml.PROP_NAME),
				typeNode: "CanvasTemplate",
				filterQuery: canvasTemplateQuery,
			});

			// build Canvas Template menu
			this.getPlugin("addPage").dropDown.buildDropdown();

			const selectCanvasTemplateAction = lang.mixin(new Action(), {
				m_id: ACTION_FROM_TEMPLATE,
				classController: "csi/action/SelectOneController",
				m_fixedQuery: canvasTemplateQuery,
				m_postAction: function(ac) {
					const canvasTemplate = ac.m_formData.getSelectedNode();
					const view = ac.widget;

					if (canvasTemplate.Pages && canvasTemplate.Pages.length) {
						const pages = ac.m_cache.getNodes(canvasTemplate.Pages);
						view.addTemplatePages(canvasTemplate, pages);
					} else {
						view.addPage();
					}
				},
				m_viewRef: lang.mixin(new ViewRef(), {
					m_id: "CanvasTemplatesGallery",
					m_url: "centric://REFLECTION/INSTANCE/_CS_Data/null"
				})
			});

			this.defView._addViewAction(selectCanvasTemplateAction);
		},

		/**
		 * Show or hide blank canvas while deleting last and adding first canvas
		 * @param {boolean} [show]
		 * @param {string} [nodeUrl]
		 */
		toggleBlankCanvas: function(show, nodeUrl) {
			if (nodeUrl) {
				var refNode = this.getRefNodeByUrl(nodeUrl);
				html.setVisible(refNode, !show);
			}

			if (this._dndSources && !this.getVisibleNodes().length || !show) {
				$(this.containerNode).toggleClass("csi-no-canvas", show);
			}
		},

		/**
		 * Switch between canvas and sets svg content based on the node selected.
		 * @param {Object} popResult
		 * @param {string} opsType
		 */
		switchToStackView: function(popResult, opsType) {
			var refNode = this.getRefNodeByUrl(popResult.id);
			var nodeSelected = this.defView.m_formData.getSelectedNode();
			var urlSelected = nodeSelected && nodeSelected.$URL;
			var canvas = this.canvasWidgetNode;
			var ops = popResult.ops;
			var self = this;

			function deletePage() {
				// redo delete operation - find next page to select (much like deletePage)
				var urlDeleted = popResult.id;

				if (urlSelected == urlDeleted) {
					urlSelected = self._getNextPage(urlDeleted);
				}

				// remove deleted page from store and view
				self.svgStore.remove(urlDeleted);
				html.setVisible(refNode, false);

				// find next page to show - if there is another operation on the stack, then switch to that page assuming it will be done next (and is visible);
				// otherwise, switch to next visible page (or previous page if on last page)
				if (urlSelected) {
					var prevResult = _.last(canvas.opsStack[opsType]);
					popResult = prevResult ? self.svgStore.get(prevResult.id) : null;
					popResult = self.svgStore.get(urlSelected);
				} else {
					popResult = null;
				}

				if (!popResult) {
					self.toggleBlankCanvas(true);
				}
			}

			function sort() {
				// fallback to original list when last dnd operation is reached
				var sortList = popResult ? popResult.sortList : canvas.originalSortList;

				var dndContainer = $(self.contentNode.domNode);
				var cells = dndContainer.children('.dojoDndItem');

				cells.detach().sort(function(a, b) {
					var aUrl = self.getQueryResultByDomNode(a).url;
					var bUrl = self.getQueryResultByDomNode(b).url;

					if (sortList.indexOf(aUrl) < sortList.indexOf(bUrl)) {
						return -1;
					} else {
						if (sortList.indexOf(aUrl) > sortList.indexOf(bUrl)) {
							return 1;
						} else {
							return 0;
						}
					}
				});

				dndContainer.append(cells);
			}

			if (opsType == "undo") {
				if (ops == "new") {
					// remove the new page from the store
					deletePage();
				} else if (ops == "delete") {
					// restore previously deleted page
					this.toggleBlankCanvas(false, popResult.id);
					this.updateSvgStore(popResult, true);

					// re-sort in case used DnD after deleting
					sort();
				} else if (ops == "dnd") {
					// restore previous sort list
					popResult = _.last(canvas.opsStack[opsType]);
					sort();
					return;
				} else {
					// restore the previous state
					var previousState = _.where(canvas.opsStack[opsType], {id: popResult.id});
					popResult = _.last(previousState) || canvas.originalContent[popResult.id];
					this.updateSvgStore(popResult, true);
				}
			} else if (ops == "delete") {
				// redo delete
				deletePage();
			} else if (ops == "dnd") {
				// redo dnd
				sort();
				return;
			} else {
				// redo other
				this.toggleBlankCanvas(false);
				this.updateSvgStore(popResult, true);
			}

			canvas.set("content", popResult);

			// switch to new canvas selection if required
			if (popResult) {
				this.changeCanvasSelection(popResult.id);
				html.setVisible(this.getRefNodeByUrl(popResult.id), true);
			}
		},

		/**
		 * Make thumbnail visible to the given url.
		 * @param {string} url URL for the page.
		 * @param {boolean} bQuiet Does not invoke onSelect.
		 */
		makeThumbnailVisible: function(url, bQuiet) {
			this.changeCanvasSelection(url, bQuiet);
			var refNode = this.getRefNodeByUrl(url);
			$(this.fadeNode).find('.' + CSS_SELECTED).removeClass(CSS_SELECTED);
			this.scrollIntoView(refNode);
			$(refNode).addClass(CSS_SELECTED);
		},

		/**
		 * Keep height of right pane in sync with height of canvas, and cap height at canvas height rather than include thumbnails height
		 */
		updatePaneHeight: function() {
			const {
				canvasContainerNode,
				defView,
				pageNode,
				splashCanvasNode,
				toolbarNode,
				viewLayout,
			} = this;
			const {domNode} = toolbarNode;
			const pageBottom = html.pageBottom();
			const pageBottomBound = pageBottom.getBoundingClientRect();
			const canvasContainerNodeBound = canvasContainerNode.getBoundingClientRect();
			// nothing to do while printing
			if (!toolbarNode) {
				return;
			}

			let height;
			if (defView.isSlim()) {
				// in composite view, so just get height of canvas page
				const h = pageNode.offsetHeight;
				if (h) {
					// either use position of footer or height of pageNode to determine height
					// if using position of footer, subtract 10 pixels for margin on bottom and 2px for border
					height = Math.max(h + html.getHorizontalScrollbarHeight(),
						pageBottomBound.top - canvasContainerNodeBound.top - 10 - 2);
				} else {
					height = splashCanvasNode.offsetHeight;
				}
			} else {
				// get available height in tab; subtract 10 to give 10px margin to page bottom
				height = html.getAvailableHeight(canvasContainerNode) - 10;
			}

			html.setPixels(canvasContainerNode, html.HEIGHT, height);

			// update height of right pane, subtracting 1 so bottom border shows
			const toolbarHeight = height - 1;
			const canvasToolbarNode = domNode.querySelector(".csi-canvas-toolbar");
			const canvasRightMenuNode = domNode.querySelector(".csi-canvas-right-menu");
			html.setPixels(canvasToolbarNode, html.HEIGHT, toolbarHeight);
			html.setPixels(canvasRightMenuNode, html.HEIGHT, toolbarHeight);
		},

		/* return true if any snapshots are missing */
		isMissingSnapshots() {
			return this.getVisibleNodes().some(url => {
				const node = this.cacheView.getNode(url);
				return !xml.isValidURL(node.Snapshot);
			});
		},

		/**
		 * Update store with new content
		 * @param {CanvasContent} content
		 * @param {boolean} bUpdateThumbnail - if true, will update all the thumbnails
		 */
		updateSvgStore: function(content, bUpdateThumbnail) {
			this.svgStore.put(content);

			if (bUpdateThumbnail) {
				this.updateThumbnail(content);
			}

			// update the indices
			this.updateThumbnailIndices();
		},

		/**
		 * Update thumbnails indices
		 */
		updateThumbnailIndices: function() {
			var svgStore = this.svgStore;
			var index = 0;
			_.each(this.getRefNodes(), function(refNode) {
				var jNode = $(refNode);
				var url = this.getRefNodeUrl(refNode);

				if (svgStore.get(url)) {
					jNode.find("." + CSS_THUMBNAIL_INDEX).html(++index);
				}
			}, this);
		},

		/**
		 * Update thumbnails display with latest content and indices
		 */
		updateThumbnails: async function() {
			if (this.bInitCanvas) {
				return;
			}

			const svgStore = await this.svgStore.query();
			for (const entry of svgStore) {
				this.updateThumbnail(entry);
			}

			// update the indices
			this.updateThumbnailIndices();
		},

		/**
		 * Update thumbnail display with latest content and indices
		 * @param {Object} entry - entry from the svgStore
		 */
		updateThumbnail: function(entry) {
			const {id} = entry;
			const refNode = this.getRefNodeByUrl(id);
			if (!refNode || !this.canvasAndNotesNode || !this.canvasAndNotesNode.clientWidth || (entry.svg == null)) {
				return;
			}
			const zoom = this._getThumbnailZoom();
			const bVertical = this.hasVerticalOrientation();
			const svgNode = refNode.querySelector('.csi-canvas-thumbnail-svg');
			const svgNodeWrapper = svgNode.querySelector('.csi-canvas-thumbnail-wrapper');
			// Replace images in thumbnail with unique id's so that they don't overwrite main
			// page images with smaller, blurry versions
			const thumbnailSvg = entry.svg.replace(/canvas-editor/g, id + "-canvas-editor")
				.replace(/Attribute=Viewable/g, "Attribute=Thumbnail")
				.replace(/pointer-events=\"visible\"/g, "pointer-events=\"none\"")
				.replace(/pointer-events\: visible;/g, "pointer-events: none;")
				.replace(/id=\"pageObject_matrix/g, "id=\"ddipageObject_matrix")
				.replace(/id=\"path/g, "id=\"ddipath")

			svgNodeWrapper.style.transformOrigin = "left top";
			svgNodeWrapper.style.pointerEvents = "none";

			const thumbnailWidth = this.thumbnailWidth * (bVertical ? 1 : entry.split);
			const thumbnailHeight = this.thumbnailHeight * (bVertical ? entry.split : 1);

			// here we are calculating width of the canvas for content
			// we can't use current width of canvasNode since it is for current page.

			// NOTE: using paper size instead of canvasNode.clientWidth is MUCH better performance.
			const {w} = this.getCanvasPageLayout();
			const canvasWidth = w * (bVertical ? 1 : entry.split);

			const scale = thumbnailWidth / canvasWidth;

			svgNodeWrapper.style.transform = "scale(" + scale + ")";
			html.setPixels(svgNode, html.WIDTH, thumbnailWidth);
			html.setPixels(svgNode, html.HEIGHT, thumbnailHeight);

			const splitter = this.canvasAndNotesNode.querySelector(".csi-split-pane-splitter");

			// C8-12395 - when canvas splitter all the way to the left, on click edit the
			// thumbnail is not blank, but shows canvas contents at full size, so do not add
			// the csi-show css class unless splitter position is not zero
			if (entry.split > 0.95) {
				splitter.style.zIndex = 0;
				svgNode.classList.add('csi-show');
			} else if (entry.split > 0) {
				splitter.style.zIndex = 350;
				svgNode.classList.add('csi-show');
			} else {
				//splitter is at zero, so hide the svg thumbnail and the snapshot
				svgNode.classList.remove('csi-show');
			}

			// update styling for notes
			const thumbnailContent = refNode.querySelector("." + CSS_THUMBNAIL_CONTENT);
			thumbnailContent.classList.toggle(csiHeading.CSS_COMMENT, !!entry.notes.length)
			thumbnailContent.style.zoom = zoom;

			// update innerHTML
			svgNodeWrapper.innerHTML = thumbnailSvg;
		},

		/**
		 * Reset titleblock view and canvas if refreshing
		 */
		_refreshWidget: function() {
			this._deleteViewLayout();
			this.inherited(arguments);
		},

		/**
		 * When the canvas is edited, the available size of the edit pane decreases (and vice-versa when the user stops editing).
		 * The split pane percentage needs to be updated so that the pixel size of the thumbnail pane remains constant.
		 * @param {boolean} bEdit - true if switching to edit mode
		 */
		_updateSplitterOnEdit: function(bEdit) {
			var splitNode = this.splitNode;
			var dim = splitNode.getDimensions();

			// toggle CSS class that adjusts available size
			$(this.domNode).toggleClass("csi-svg-edit", bEdit);

			if (dim) {
				splitNode.set("split", splitNode.getSplitForPixels(dim.pane1));
			}
		},

		isCanvasTemplate: function() {
			return this.getViewNodeType() == "CanvasTemplate";
		},

		isProductPresentationLayout: function() {
			return this.getViewNodeType() == "ProductPresentationLayout";
		},

		/**
		 * Check if the node type equals style BOM
		 * @returns 
		 */
		isStyleBOM() {
			return this.getViewNodeType() === "ApparelBOMRevision";
		},

		/*
		 * Return true if Canvas Widget runs under "Theme" node
		 * @return {boolean}
		 */
		isTheme: function() {
			return this.getViewNodeType() == "Theme";
		},

		/**
		 * Update store SVG data for all canvas pages each page's JSON data.
		 * @return {Promise}
		 */
		copyJSONtoSVGandUpdateThumbnails: async function() {
			const pageMap = {};
			const params = {
				editorContainer: document.createElement("div"),
				isPrimaryInstance: false,
				dontDownloadImages: true
			};
			const svgStore = await this.svgStore.query();
			for (const entry of svgStore) {
				const canvasApp = svgUtil.createCanvasApp(params, {
					onContentChange: (payload) => {
						const {pageHtml, pageId} = payload;
						const map = pageMap[pageId];
						svgUtil.removeCanvasApp(map.canvasApp);
						map.entry.svg = svgUtil.resolveCanvasAppImageHref(pageHtml);
						map.resolve();
					}
				});
				canvasApp.canvasApi().setIsCanvasTemplate(false);
				svgUtil.setCanvasAppContent(canvasApp, entry);
				await new Promise((resolve) => {
					pageMap[entry.id] = {
						canvasApp,
						entry,
						resolve,
					}
				})
			}
			for (const entry of svgStore) {
				// update svg and thumbnail for entry
				this.updateSvgStore(pageMap[entry.id].entry, true);
			}
		}
	});
});
